
import { format, addMinutes, parse, isAfter, isBefore, setHours, setMinutes, getWeek, addMonths, addDays } from "date-fns";
import { fr } from "date-fns/locale";
import { WorkingHours, TimeSlot } from "@/types";

export const formatDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, "d MMMM yyyy", { locale: fr });
};

export const formatTime = (time: string): string => {
  return format(parse(time, "HH:mm:ss", new Date()), "HH:mm");
};

export const formatDateWithDay = (date: Date): string => {
  return format(date, "EEEE d MMMM yyyy", { locale: fr });
};

export const convertDurationToMinutes = (duration: string): number => {
  const match = duration.match(/(\d+)\s*min/);
  return match ? parseInt(match[1], 10) : 60; // default to 60 minutes if not specified
};

export const calculateEndTime = (startTime: Date, durationMinutes: number): Date => {
  return addMinutes(startTime, durationMinutes);
};

export const isTimeInWorkingHours = (
  date: Date,
  time: string,
  workingHours: WorkingHours[]
): boolean => {
  const dayOfWeek = date.getDay();
  const dayHours = workingHours.find(h => h.day_of_week === dayOfWeek);
  
  if (!dayHours || !dayHours.is_working_day) return false;
  
  const timeObj = parse(time, "HH:mm", new Date());
  const startTimeObj = parse(dayHours.start_time, "HH:mm:ss", new Date());
  const endTimeObj = parse(dayHours.end_time, "HH:mm:ss", new Date());
  
  return !isBefore(timeObj, startTimeObj) && !isAfter(timeObj, endTimeObj);
};

interface RecurringBreakSlot {
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
  valid_from?: string | null;
  valid_until?: string | null;
}

export const generateTimeSlots = (
  date: Date,
  workingHours: WorkingHours[],
  appointments: { start_time: string; end_time: string; date: string }[],
  selectedServicesDuration: number,
  bookingBufferHours: number = 2,
  bufferBefore: number = 0,
  breaks: { start_time: string; end_time: string; date: string }[] = [],
  recurringBreaks: RecurringBreakSlot[] = []
): TimeSlot[] => {
  const dayOfWeek = date.getDay();
  const dayHours = workingHours.find(h => h.day_of_week === dayOfWeek);
  
  if (!dayHours || !dayHours.is_working_day) return [];
  
  const now = new Date();
  const isToday = format(date, "yyyy-MM-dd") === format(now, "yyyy-MM-dd");
  
  // Calculer le temps minimum (maintenant + buffer)
  const minimumTime = addMinutes(now, bookingBufferHours * 60);
  
  const startTime = parse(dayHours.start_time, "HH:mm:ss", date);
  const endTime = parse(dayHours.end_time, "HH:mm:ss", date);
  
  const slots: TimeSlot[] = [];
  const slotDuration = 30; // 30 minutes intervals
  
  let currentTime = startTime;
  const dateStr = format(date, "yyyy-MM-dd");
  
  // Filter appointments for the selected date
  const dayAppointments = appointments.filter(app => app.date === dateStr);
  
  // Filter breaks for the selected date (manual breaks + expanded recurring breaks)
  const expandedRecurring = recurringBreaks
    .filter((rb) => rb.is_active && rb.day_of_week === dayOfWeek)
    .filter((rb) => {
      if (rb.valid_from && dateStr < rb.valid_from) return false;
      if (rb.valid_until && dateStr > rb.valid_until) return false;
      return true;
    })
    .map((rb) => ({ start_time: rb.start_time, end_time: rb.end_time, date: dateStr }));
  const dayBreaks = [...breaks.filter((b) => b.date === dateStr), ...expandedRecurring];
  
  while (!isAfter(addMinutes(currentTime, selectedServicesDuration), endTime)) {
    const slotStart = format(currentTime, "HH:mm");
    const slotEnd = format(addMinutes(currentTime, selectedServicesDuration), "HH:mm");
    
    // Créer l'objet DateTime complet pour ce créneau (incluant le buffer avant)
    const slotDateTime = setHours(
      setMinutes(date, parseInt(slotStart.split(':')[1])), 
      parseInt(slotStart.split(':')[0])
    );
    
    // Filtrer si c'est aujourd'hui et que le créneau est trop proche ou déjà passé
    if (isToday && isBefore(slotDateTime, minimumTime)) {
      currentTime = addMinutes(currentTime, slotDuration);
      continue;
    }
    
    // Check if this slot (including buffer before) overlaps with any existing appointment
    const isOverlappingAppointment = dayAppointments.some(app => {
      const appStart = parse(app.start_time, "HH:mm:ss", date);
      const appEnd = parse(app.end_time, "HH:mm:ss", date);
      
      // Le créneau réel commence bufferBefore minutes avant l'heure affichée au client
      const actualSlotStartTime = setHours(
        setMinutes(date, parseInt(slotStart.split(':')[1]) - bufferBefore), 
        parseInt(slotStart.split(':')[0]) - Math.floor(bufferBefore / 60)
      );
      const slotEndTime = setHours(setMinutes(date, parseInt(slotEnd.split(':')[1])), parseInt(slotEnd.split(':')[0]));
      
      return (
        (isAfter(actualSlotStartTime, appStart) && isBefore(actualSlotStartTime, appEnd)) ||
        (isAfter(slotEndTime, appStart) && isBefore(slotEndTime, appEnd)) ||
        (isBefore(actualSlotStartTime, appStart) && isAfter(slotEndTime, appEnd)) ||
        (format(actualSlotStartTime, "HH:mm") === format(appStart, "HH:mm"))
      );
    });
    
    // Check if this slot overlaps with any break
    const isOverlappingBreak = dayBreaks.some(brk => {
      const breakStart = parse(brk.start_time, "HH:mm:ss", date);
      const breakEnd = parse(brk.end_time, "HH:mm:ss", date);
      
      const slotStartTime = parse(slotStart, "HH:mm", date);
      const slotEndTime = parse(slotEnd, "HH:mm", date);
      
      return (
        (isAfter(slotStartTime, breakStart) && isBefore(slotStartTime, breakEnd)) ||
        (isAfter(slotEndTime, breakStart) && isBefore(slotEndTime, breakEnd)) ||
        (isBefore(slotStartTime, breakStart) && isAfter(slotEndTime, breakEnd)) ||
        (!isBefore(slotStartTime, breakStart) && !isAfter(slotStartTime, breakStart) && isBefore(slotStartTime, breakEnd)) ||
        (format(slotStartTime, "HH:mm") === format(breakStart, "HH:mm"))
      );
    });
    
    if (!isOverlappingAppointment && !isOverlappingBreak) {
      slots.push({
        date: date,
        startTime: slotStart,
        endTime: slotEnd,
        formatted: `${slotStart} - ${slotEnd}`
      });
    }
    
    currentTime = addMinutes(currentTime, slotDuration);
  }
  
  return slots;
};

// Helper function to get week number
export const getWeekNumber = (date: Date): number => {
  return getWeek(date, { locale: fr, weekStartsOn: 1 }); // Week starts on Monday in France
};

/**
 * Trouve la première date disponible avec au moins un créneau horaire
 * @param workingHours - Horaires de travail de l'institut
 * @param appointments - Rendez-vous existants
 * @param serviceDuration - Durée totale des services sélectionnés en minutes
 * @param maxDaysToCheck - Nombre maximum de jours à vérifier (défaut: 60)
 * @returns La première date avec des créneaux disponibles
 */
export const findNextAvailableDate = (
  workingHours: WorkingHours[],
  appointments: { start_time: string; end_time: string; date: string }[],
  serviceDuration: number = 60,
  maxDaysToCheck: number = 60,
  bookingBufferHours: number = 2,
  bufferBefore: number = 0,
  breaks: { start_time: string; end_time: string; date: string }[] = [],
  recurringBreaks: RecurringBreakSlot[] = []
): Date => {
  let currentDate = new Date();
  let daysChecked = 0;

  while (daysChecked < maxDaysToCheck) {
    const slots = generateTimeSlots(currentDate, workingHours, appointments, serviceDuration, bookingBufferHours, bufferBefore, breaks, recurringBreaks);

    if (slots.length > 0) {
      return currentDate;
    }

    currentDate = addDays(currentDate, 1);
    daysChecked++;
  }

  return new Date();
};

// Calculate gift card expiration date (6 months from payment confirmation)
export const calculateGiftCardExpiryDate = (): string => {
  const paymentDate = new Date();
  const expiryDate = addMonths(paymentDate, 6);
  return format(expiryDate, "yyyy-MM-dd");
};
